/**
 * useShellFollowTurn - FOLLOW THE TURN (v8.324.0), a viewer preference.
 *
 * When a TURN ENDS, the actor pointer may follow - the GM's always, a
 * player's after their own turn or onto a unit they control
 * (`engine/turnFollow`). Only a turn ending counts: an arrival, a re-sort or
 * a removal also changes who is active, and following those stole the
 * pointer from a player who had just picked their character (v8.327.1).
 * Opt-in (v8.331.5): unset means off.
 *
 * Only the order's movement may trigger it; the pointer and the roster are
 * READ at that moment, not watched - so they reach the effect through a ref
 * refreshed every render, which is what the effect's closure saw anyway.
 * Extracted from DeathWatchSimulator.
 */
import { useEffect, useRef } from 'react';
import { followTurnTarget, turnAdvanced } from '../engine/turnFollow';

/** The participant whose turn it is, or null with the tracker off. */
export function activeParticipantOf(trackerState) {
  return (trackerState.active && trackerState.participants[trackerState.activeIndex]) || null;
}

/** The turn as a comparison point: round, active participant, active combatant. */
function turnPoint(trackerState) {
  const participant = activeParticipantOf(trackerState);
  return {
    round: trackerState.round,
    activeParticipantId: participant?.id ?? null,
    activeCombatantId: participant?.combatantId ?? null,
  };
}

/** Who the actor should become after this change of order, or null to stay. */
function followAfter(previous, trackerState, seen) {
  const current = turnPoint(trackerState);
  const advanced = trackerState.active && turnAdvanced(previous, trackerState);
  if (!advanced || previous.activeCombatantId === current.activeCombatantId) return null;
  return followTurnTarget({
    enabled: seen.followTurn,
    isGm: seen.isGm,
    previousActiveId: previous.activeCombatantId,
    nextActiveId: current.activeCombatantId,
    actorId: seen.actorId,
    roster: seen.roster,
  });
}

/** @param {object} args - trackerState, followTurn, isGm, actorId, roster, setActorId. */
export default function useShellFollowTurn({ trackerState, ...seen }) {
  const previousTurnRef = useRef(turnPoint(trackerState));
  const seenRef = useRef(seen);
  seenRef.current = seen;
  useEffect(() => {
    const previous = previousTurnRef.current;
    previousTurnRef.current = turnPoint(trackerState);
    const next = followAfter(previous, trackerState, seenRef.current);
    if (next) seenRef.current.setActorId(next);
  }, [trackerState]);
}
